import React, { useEffect } from "react";
import { CheckCircle2, ShieldCheck, Wrench, MapPin, Award, Star } from "lucide-react";
import Seo from "../components/site/Seo";
import MetaLeadForm from "../components/site/MetaLeadForm";
import MeetTyler from "../components/site/sections/MeetTyler";
import Reveal from "../components/site/Reveal";
import { IMG } from "../lib/site";
import { captureAttribution } from "../lib/attribution";

const HERO_TRUST = ["Premium, Industrial-Grade Coatings", "Professional Installation", "Licensed, Bonded & Insured"];

const BENEFITS = [
  { icon: Award, title: "Premium Materials", desc: "Built for long-term durability" },
  { icon: Wrench, title: "Professional Installation", desc: "Installed by the PRYME team" },
  { icon: MapPin, title: "Local Company", desc: "Serving Northern Arizona" },
  { icon: ShieldCheck, title: "Lifetime Residential Warranty", desc: "For as long as you own your home" },
];

const GALLERY = [
  IMG.blackGarage, IMG.grayFlake, IMG.metallic,
  IMG.flakeTruck, IMG.hexLed, IMG.metallicRedShop,
];

const TRUST_POINTS = [
  "Licensed, Bonded & Insured", "Locally Owned & Operated", "Premium Materials",
  "Lifetime Residential Warranty", "Proper Concrete Preparation", "Never Cutting Corners",
];

const btnPrimary = "";
const MetaLanding = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    captureAttribution();
  }, []);

  return (
    <div className="bg-[#0A0A0A] min-h-screen text-white">
      <Seo
        title="Garage Floor Coatings in Northern Arizona | Free Estimate | PRYME EPOXY"
        description="Premium garage floor coatings professionally installed by PRYME EPOXY across Northern Arizona. Licensed, bonded, insured. Lifetime residential warranty. Get a free estimate."
        path="/lp/meta"
        robots="noindex, nofollow"
      />

      {/* 1 + 2. HERO with form */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={IMG.blackGarage} alt="Premium PRYME EPOXY garage floor coating" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/80 to-black/70" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-10 py-12 lg:py-20 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div className="text-center lg:text-left">
            <Reveal delay={0.08}>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light leading-tight mt-4">
                Transform Your Garage Into a Space You're <span className="text-[#C69453] font-semibold">Proud Of</span>
              </h1>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="text-lg md:text-xl text-zinc-300 mt-5 max-w-xl mx-auto lg:mx-0">
                Premium garage floor coatings professionally installed by PRYME EPOXY — now up to <span className="text-white font-semibold">$700 off</span> your project.
              </p>
            </Reveal>
            <Reveal delay={0.24}>
              <ul className="mt-7 space-y-3 inline-block text-left">
                {HERO_TRUST.map((t) => (
                  <li key={t} className="flex items-center gap-3 text-zinc-200">
                    <CheckCircle2 className="w-5 h-5 text-[#C69453] shrink-0" /> <span className="font-medium">{t}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="mt-8 text-[#C69453] text-sm font-semibold tracking-widest uppercase lg:hidden">↓ Claim your $700 offer below ↓</p>
            </Reveal>
          </div>

          <Reveal delay={0.16}>
            <MetaLeadForm />
          </Reveal>
        </div>
      </section>

      {/* WORK PROOF (moved above benefit bar) */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-[#C69453] text-sm font-semibold tracking-[0.2em] uppercase">Our Work</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white mt-2">Real Projects. Real Results.</h2>
            <p className="text-zinc-400 mt-4">See what PRYME EPOXY can do for your garage.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {GALLERY.map((src, i) => (
              <div key={i} className="overflow-hidden border border-zinc-800">
                <img src={src} alt={`PRYME EPOXY completed floor coating project ${i + 1} in Northern Arizona`} loading="lazy" className="w-full object-cover aspect-square hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MEET THE FAMILY */}
      <MeetTyler showPhone={false} centered />

      {/* QUICK BENEFIT BAR */}
      <section className="bg-zinc-950 border-y border-zinc-900">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 lg:divide-x divide-zinc-900">
          {BENEFITS.map((b) => (
            <div key={b.title} className="flex flex-col items-center text-center gap-2 py-8 px-4">
              <b.icon className="w-8 h-8 text-[#C69453]" />
              <div className="font-bold text-white text-sm uppercase tracking-wide">{b.title}</div>
              <div className="text-zinc-400 text-xs">{b.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* 5. SOCIAL PROOF / TRUST */}
      <section className="py-16 md:py-20 bg-zinc-950 border-y border-zinc-900">
        <div className="max-w-6xl mx-auto px-5 lg:px-10">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center">Why Northern Arizona Homeowners Choose PRYME</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
            {TRUST_POINTS.map((t) => (
              <div key={t} className="flex items-center gap-3 border border-zinc-800 bg-[#141414] px-5 py-5">
                <CheckCircle2 className="w-6 h-6 text-[#C69453] shrink-0" />
                <span className="text-white font-semibold">{t}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-center gap-3 mt-12 flex-wrap text-center">
            <div className="flex text-yellow-400">{[...Array(5)].map((_, i) => <Star key={i} className="w-6 h-6 fill-current" />)}</div>
            <p className="text-zinc-300">Rated <strong>5.0 stars</strong> by our Northern Arizona customers on Google</p>
          </div>
        </div>
      </section>

      {/* 6. BRAND / DIFFERENTIATOR */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-5 lg:px-10 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white">Your Garage Is Part of Your Home. <span className="text-[#C69453]">Treat It Like It.</span></h2>
          <div className="text-zinc-300 text-lg leading-relaxed mt-6 space-y-4">
            <p>Your home is one of your biggest investments. Your garage shouldn't feel like an afterthought.</p>
            <p>PRYME installs premium concrete coating systems designed to create a cleaner, more durable, better-looking space that you'll actually be proud to show off.</p>
            <p>We're a local Northern Arizona company—not a national franchise—and we believe in doing the job right instead of cutting corners.</p>
          </div>
        </div>
      </section>

      {/* 7. FINAL CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={IMG.metallic} alt="PRYME EPOXY metallic garage floor" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/80" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-5 lg:px-10 py-20 md:py-28 text-center">
          <h2 className="text-3xl md:text-5xl font-light text-white">Ready to Transform Your Garage?</h2>
          <p className="text-zinc-300 text-lg mt-4">Get your free estimate from PRYME EPOXY and claim up to <span className="text-[#C69453] font-semibold">$700 off</span> your garage floor.</p>
          <p className="text-zinc-400 text-sm mt-8">Serving Prescott, Prescott Valley, Sedona, Flagstaff, Cottonwood and surrounding Northern Arizona communities.</p>
        </div>
      </section>
    </div>
  );
};

export default MetaLanding;
